
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { devLog, devError } from '@/utils/devLog';
import { useQueryClient } from '@tanstack/react-query';
import { getPendingEnrollment, clearPendingEnrollment } from '@/utils/pendingEnrollment';

export function usePendingEnrollmentRedirect() {
  const navigate = useNavigate();
  const { session, user, loading } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only run this logic when auth state is settled and we have a user
    if (loading) return;
    
    const processPendingEnrollment = async () => {
      try {
        // Check if there's a pending enrollment in localStorage
        const pendingEnrollment = getPendingEnrollment();
        if (!pendingEnrollment || !pendingEnrollment.courseId) {
          return;
        }
        
        devLog("[PendingEnrollmentRedirect] Found pending enrollment", pendingEnrollment);
        
        // If user is not logged in, redirect to student-auth
        if (!user || !session) {
          devLog("[PendingEnrollmentRedirect] User not authenticated, redirecting to student-auth");
          navigate('/student-auth', { replace: true });
          return;
        }
        
        devLog("[PendingEnrollmentRedirect] Processing pending enrollment for authenticated user", {
          userId: user.id,
          courseId: pendingEnrollment.courseId
        });
        
        // Check if already enrolled
        const { data: isEnrolled } = await supabase.rpc(
          'get_enrollment_status',
          { p_user_id: user.id, p_course_id: pendingEnrollment.courseId }
        );
        
        if (isEnrolled) {
          devLog("[PendingEnrollmentRedirect] User already enrolled in course");
          toast.info("You're already enrolled in this course!");
          clearPendingEnrollment();
          navigate(`/dashboard/student/my-learning/course/${pendingEnrollment.courseId}`, { replace: true });
          return;
        }
        
        // Handle free course immediate enrollment
        const priceNum = Number(pendingEnrollment.price);
        if (!isNaN(priceNum) && priceNum <= 0) {
          devLog("[PendingEnrollmentRedirect] Processing free course enrollment", {
            courseId: pendingEnrollment.courseId,
            price: priceNum
          });
          
          const { error: enrollError } = await supabase
            .from('enrollments')
            .upsert({
              user_id: user.id,
              course_id: pendingEnrollment.courseId,
              status: 'active'
            }, {
              onConflict: 'user_id,course_id'
            });
            
          if (enrollError) {
            throw new Error(`Failed to enroll: ${enrollError.message}`);
          }
          
          // Invalidate enrollments query
          queryClient.invalidateQueries({ queryKey: ['enrollments'] });
          
          // Clear the pending enrollment
          clearPendingEnrollment();
          
          toast.success("Successfully enrolled in course!");
          navigate(`/dashboard/student/my-learning?from_free=true&course_id=${pendingEnrollment.courseId}`, { replace: true });
          return;
        }
        
        // For paid courses, redirect to checkout
        devLog("[PendingEnrollmentRedirect] Redirecting to checkout for paid course");
        
        // If username and courseSlug are available, use the course-specific checkout
        if (pendingEnrollment.username && pendingEnrollment.courseSlug) {
          const checkoutUrl = `/${pendingEnrollment.username}/${pendingEnrollment.courseSlug}/checkout`;
          devLog("[PendingEnrollmentRedirect] Using course-specific checkout", { 
            checkoutUrl,
            username: pendingEnrollment.username,
            courseSlug: pendingEnrollment.courseSlug
          });
          
          // Don't clear enrollment yet - it will be handled after checkout
          navigate(checkoutUrl, { replace: true });
        } else {
          // Fall back to global checkout
          devLog("[PendingEnrollmentRedirect] Using global checkout (no username/courseSlug)");
          navigate(`/global-checkout`, { 
            replace: true,
            state: { 
              courseId: pendingEnrollment.courseId,
              courseName: pendingEnrollment.courseName,
              coursePrice: pendingEnrollment.price
            }
          });
        }
      } catch (error) {
        devError("[PendingEnrollmentRedirect] Error processing enrollment:", error);
        toast.error("Something went wrong with your enrollment. Please try again.");
        clearPendingEnrollment();
      }
    };

    processPendingEnrollment();
  }, [navigate, session, user, loading, queryClient]);
  
  return null;
}
